import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import NavFooter from '../components/NavFooter';
import Footer from '../components/Footer';
import FolderModal from '../components/FolderModal';
import PaymentModal from '../components/PaymentModal';
import SkipSelfAssessment from '../components/SkipSelfAssessment';
import { APP_ROUTES } from '../route';
import { getUserAttribute, isLoggedIn } from '../utils/helpers';
import {
  /* doToggleEmailModel, */ setSelectedFilters,
} from '../store/action/common';
import { LOCAL_STORAGE_CONSTANT } from '../constants/app';
import { extractJSON } from '../services/jsonUtil';
import Tour from '../components/Tour';
import EmailModal from '../components/EmailModal';
import SeoTags from '../components/SeoComponent';
import { getCanonicalLink, SeoTexts } from '../services/SEO';
import { encodeBase64Value } from '../utils/url';

// Rendered as a route `element` rather than wrapping <Route> itself. `path` is
// passed in explicitly because v6 gives an element no way to ask which route
// pattern matched it, and several of the checks below key off that pattern.
const PrivateRoute = props => {
  let {
    activeSection,
    component: Component,
    enableAutoPlay,
    hideFooter,
    path,
  } = props;

  const dispatch = useDispatch();
  const location = useLocation();
  const { search } = location;
  const searchParams = new URLSearchParams(search);
  const orderParam = searchParams.get('order');
  const onBoardingCompleted = getUserAttribute('onboarding_completed');
  const isProfileRoute =
    path === APP_ROUTES.PROFILE || path === APP_ROUTES.PROFILE_WITH_SUB_ROUTES;
  const showSelfAssessmentBtn =
    path.includes(APP_ROUTES.SELF_ASSESSMENT) && !onBoardingCompleted;

  if (!onBoardingCompleted && !isProfileRoute) {
    hideFooter = true;
  }

  useEffect(() => {
    const retreivedFilters =
      extractJSON(LOCAL_STORAGE_CONSTANT.SELECTED_FILTERS) || [];
    if (retreivedFilters?.length) {
      dispatch(
        setSelectedFilters({
          selectedFilters: retreivedFilters,
          skipSave: true,
        }),
      );
    }
  }, []);

  const inOnBoardingFlow = (() => {
    switch (path) {
      case APP_ROUTES.SELF_ASSESSMENT:
      case APP_ROUTES.FITNESS:
      case APP_ROUTES.PROFILE:
      case APP_ROUTES.PROFILE_WITH_SUB_ROUTES:
      case APP_ROUTES.SELF_ASSESSMENT_DETAILED_VIDEO:
        return true;
      default:
        return false;
    }
  })();

  const hasRedirectedToOnboarding =
    sessionStorage.getItem('onboardingRedirected') === 'true';

  // JW-210 : Only redirect to onboarding ONCE per session to avoid the onboarding loop
  if (
    isLoggedIn() &&
    !onBoardingCompleted &&
    !inOnBoardingFlow &&
    !orderParam &&
    !hasRedirectedToOnboarding
  ) {
    sessionStorage.setItem('onboardingRedirected', 'true');

    return (
      <Navigate
        to={{
          pathname: APP_ROUTES.FITNESS,
          search: search
            ? `?page=${encodeBase64Value(location.pathname + search)}`
            : '',
        }}
        state={{ from: location }}
        replace
      />
    );
  }

  if (!isLoggedIn()) {
    return <Navigate to={APP_ROUTES.ROOT} state={{ from: location }} replace />;
  }

  return (
    <Tour>
      <div
        id="main-body"
        className={clsx(
          'main-body',
          hideFooter && !showSelfAssessmentBtn && 'h-100 mh-100',
        )}
      >
        <Component enableAutoPlay={enableAutoPlay} />
        {showSelfAssessmentBtn && (
          <SkipSelfAssessment forDisplays={['ipad', 'mobile']} />
        )}
        <FolderModal />
      </div>
      {showSelfAssessmentBtn && (
        <SkipSelfAssessment forDisplays={['desktop']} />
      )}
      {!hideFooter && <NavFooter activeSection={activeSection} />}
      {!hideFooter && <Footer />}
      <PaymentModal />
      <EmailModal path={path} />
      <SeoTags
        title={SeoTexts.JEN_WEB_TITLE}
        canonicalLink={getCanonicalLink()}
        metaDescription={SeoTexts.JEN_WEB_DESCRIPTION}
        ogImgUrl={SeoTexts.JEN_WEB_OG_IMAGE_URL}
        twitterImgUrl={SeoTexts.JEN_WEB_TWITTER_IMAGE_URL}
      />
    </Tour>
  );
};

PrivateRoute.propTypes = {
  activeSection: PropTypes.string,
  component: PropTypes.any,
  enableAutoPlay: PropTypes.bool,
  hideFooter: PropTypes.bool,
  path: PropTypes.string.isRequired,
};

export default PrivateRoute;
